import React from "react";
import { Box, Typography } from "@mui/joy";
import { theme } from "styles/theme";

// =========================
// PresetScenarios — clickable bubbles to send canned messages
// =========================

export interface PresetScenario {
  id: string;
  label: string;
  message: string;
  description?: string;
}

const RAW_TEXT_EXPERIENCE_REVIEW = `'경험 분리'를 복습하면서 마무리하는 활동지 하나 만들어줘. 아래 내용 참고하고, 중간중간 이해 확인용 퀴즈도 적절히 넣어줘.

경험 분리가 뭐냐면, 어떤 상황에 부딪혔을 때 생각·감정·신체 감각·행동(충동)이 한 덩어리로 뭉쳐서 밀려오는 걸 '경험 꾸러미'라고 하는데, 여기에 휩쓸리지 않으려면 그 덩어리를 개별 요소로 쪼개서 봐야 한다는 거야. 참고로 '상황'은 꾸러미 구성 요소가 아니라 그걸 촉발하는 외부 자극임.

기록할 때 요소별 요령:
- 상황: 주관적 평가 빼고 객관적 사실만. 긴 '선' 말고 짧은 '점'으로, 지금 사건 중심으로.
- 생각: 그 찰나에 스친 날것의 독백 두세 개. 다듬은 말투나 대화체 말고 "또 망쳤네" 같은 혼잣말로.
- 감정·신체 반응: 문장 말고 명사형 단어로. 감정은 '슬픔', 신체 반응은 '식은땀'처럼. 둘이 헷갈리지 않게.
- 행동·충동: 심호흡 같은 의도적 대처 말고, 무의식적으로 튀어나온 반응이나 속에서 솟구친 충동으로.

마지막은 이제 <생각기록지>에서 스스로 연습하게 된다는 안내로 마무리해줘.`;

export const PRESET_SCENARIOS: PresetScenario[] = [
  {
    id: "exampleTask",
    label: "예시 활동지 만들어줘",
    message: RAW_TEXT_EXPERIENCE_REVIEW,
    description:
      "경험 분리 배경지식과 요령을 전달하고 활동지로 만들어달라고 요청",
  },
  {
    id: "addQuiz",
    label: "퀴즈 추가해줘",
    message: "맥락에 맞는 자리에 어울리는 퀴즈를 하나 추가해줘",
    description: "활동지 흐름에 맞는 위치·내용으로 퀴즈를 추가해달라고 요청",
  },
  {
    id: "addTextInput",
    label: "텍스트 인풋 추가해줘",
    message: "맥락에 맞는 자리에 어울리는 텍스트 입력 칸을 추가해줘",
    description:
      "활동지 흐름에 맞는 위치·내용으로 텍스트 입력 칸을 추가해달라고 요청",
  },
  {
    id: "addImage",
    label: "이미지 추가해줘",
    message: "맥락에 맞는 자리에 어울리는 이미지를 하나 추가해줘",
    description: "활동지 흐름에 맞는 위치·내용으로 이미지를 추가해달라고 요청",
  },
  {
    id: "validate",
    label: "검수 해줘",
    message: "현재 활동지를 규칙대로 검수해줘",
    description: "현재 활동지를 v2 규칙대로 검수해달라고 요청",
  },
  {
    id: "help",
    label: "도움말",
    message: "도움말",
    description: "Agent 사용법과 역할 안내를 요청",
  },
];

export function PresetScenariosBar({
  onPick,
  disabled,
}: {
  onPick: (scenario: PresetScenario) => void;
  disabled: boolean;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        px: "12px",
        pt: "8px",
        pb: "4px",
      }}
    >
      <Typography
        level="body-xs"
        sx={{
          fontSize: "10px",
          color: theme.vars.palette.Content.Weak,
          alignSelf: "center",
          mr: "2px",
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        시나리오
      </Typography>
      {PRESET_SCENARIOS.map((scenario) => (
        <Box
          key={scenario.id}
          onClick={() => !disabled && onPick(scenario)}
          title={scenario.description}
          sx={{
            px: "10px",
            py: "4px",
            borderRadius: "999px",
            backgroundColor: theme.vars.palette.Background.Level2,
            border: `1px solid ${theme.vars.palette.Border.Weak}`,
            fontSize: "11px",
            fontWeight: 500,
            color: theme.vars.palette.Content.Medium,
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.5 : 1,
            transition: "all 0.15s ease",
            userSelect: "none",
            "&:hover": disabled
              ? {}
              : {
                  backgroundColor: theme.vars.palette.Background.Level1,
                  borderColor: theme.vars.palette.Content.Medium,
                  color: theme.vars.palette.Content.Strong,
                  transform: "translateY(-1px)",
                },
          }}
        >
          {scenario.label}
        </Box>
      ))}
    </Box>
  );
}
